/**
 * ALDER SEARCH — Privacy Policy Page
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const sections = [
  {
    title: "Who We Are",
    content: `Alder Search Ltd is a retained product executive search firm registered in England and Wales. Our registered office is at 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ. We operate exclusively in the VC and PE sector, placing product leaders and senior practitioners with VC and PE investors and their portfolio companies.

When we refer to "Alder Search", "we", "us", or "our" in this policy, we mean Alder Search Ltd as the data controller responsible for your personal information.`,
  },
  {
    title: "Information We Collect",
    content: `We collect personal information in the following ways:

Directly from you: When you submit an enquiry through our website contact form, we collect your name, email address, and any other information you choose to provide, including your firm name, role, and the nature of your enquiry. When you request a downloadable guide, we collect your email address, the resource requested, and your consent to receive the guide and relevant product-career insights. When you complete the ARR Hiring Gap Check, we collect your assessment responses, company context, referral and campaign information, name, work email, role, optional company and LinkedIn details, and your consent to receive the report and relevant product hiring insights.

From publicly available sources: As part of our executive search work, we may collect information about professionals from publicly available sources, including LinkedIn, company websites, industry publications, and professional directories.

From referrals: We may receive your contact details from colleagues, clients, or other professionals who believe our services may be relevant to you.

Automatically: Our website uses standard analytics tools to collect anonymised data about how visitors use the site, including pages visited, time spent, and referring sources. This data does not identify you personally.`,
  },
  {
    title: "How We Use Your Information",
    content: `We use personal information for the following purposes:

Responding to enquiries: When you contact us through our website, we use your information to respond to your enquiry and, where relevant, to follow up on the conversation.

Delivering requested guides and reports: When you request a downloadable guide or ARR Hiring Gap Check report, we use your email address to provide the requested resource. Where you have opted in, we may also send relevant product-career insights. You can withdraw this consent at any time by using the unsubscribe link in the email or by contacting engage@aldersearch.com. An unsubscribe request stops future ARR Hiring Gap Check follow-up emails immediately.

Executive search services: Where you are a candidate or potential candidate, we use your information to assess your suitability for specific roles, to present your profile to clients (with your knowledge and consent), and to manage the search process.

Client relationship management: Where you are a client or prospective client, we use your information to manage our relationship, to communicate about active mandates, and to keep you informed of relevant market developments.

Legal and regulatory compliance: We may use your information where required to comply with applicable laws and regulations.

We do not sell your personal information to third parties, and we do not use it for automated decision-making or profiling.`,
  },
  {
    title: "Legal Basis for Processing",
    content: `We process your personal information on the following legal bases under UK GDPR:

Legitimate interests: For most of our executive search activities, we rely on our legitimate interests (and those of our clients) in identifying and placing suitable candidates for senior roles. We balance these interests against your rights and interests when determining whether this basis is appropriate.

Contract performance: Where we have a formal engagement with you as a client or candidate, we process your information as necessary to fulfil that engagement.

Consent: Where we rely on your consent, you have the right to withdraw it at any time by contacting us at engage@aldersearch.com.

Legal obligation: Where we are required to process your information to comply with a legal obligation.`,
  },
  {
    title: "How We Share Your Information",
    content: `We share personal information only in the following circumstances:

With clients: As part of our executive search work, we share candidate information with client organisations. We do this only with the candidate's knowledge and, where appropriate, consent.

With service providers: We use a small number of trusted service providers to support our operations, including Resend for email delivery, Google Sheets and Google Apps Script for the ARR Hiring Gap Check lead log, Manus for website hosting and application infrastructure, and analytics providers for anonymised website measurement. These providers are contractually bound to process data only on our instructions and in accordance with applicable data protection law.

For legal reasons: We may disclose information where required by law, regulation, or legal process, or where necessary to protect the rights, property, or safety of Alder Search, our clients, or others.

We do not transfer personal data outside the United Kingdom or European Economic Area except where adequate protections are in place.`,
  },
  {
    title: "How Long We Keep Your Information",
    content: `We retain personal information for as long as necessary to fulfil the purposes for which it was collected, and in accordance with our legal and regulatory obligations.

For candidates: We typically retain candidate information for up to five years from the date of last contact, unless you ask us to remove it sooner or we have an ongoing relationship that justifies longer retention.

For clients: We retain client information for the duration of our relationship and for a period of up to seven years thereafter, in line with standard commercial record-keeping requirements.

For website enquiries: We retain enquiry data for up to two years from the date of the enquiry.

You may request deletion of your information at any time by contacting us at engage@aldersearch.com.`,
  },
  {
    title: "Your Rights",
    content: `Under UK GDPR, you have the following rights in relation to your personal information:

Right of access: You may request a copy of the personal information we hold about you.

Right to rectification: You may ask us to correct inaccurate or incomplete information.

Right to erasure: You may ask us to delete your personal information in certain circumstances.

Right to restriction: You may ask us to restrict the processing of your information in certain circumstances.

Right to data portability: You may ask us to provide your information in a structured, machine-readable format.

Right to object: You may object to processing based on legitimate interests, including for direct marketing purposes.

Right to withdraw consent: Where we rely on consent, you may withdraw it at any time.

To exercise any of these rights, please contact us at engage@aldersearch.com. We will respond within one month of receiving your request. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.`,
  },
  {
    title: "Cookies",
    content: `Our website uses cookies and similar technologies to improve your experience and to understand how the site is used. We use analytics cookies (via standard analytics platforms) to collect anonymised data about site usage. These cookies do not identify you personally.

You can control cookies through your browser settings. Disabling cookies may affect the functionality of the website.`,
  },
  {
    title: "Security",
    content: `We take the security of your personal information seriously. We implement appropriate technical and organisational measures to protect against unauthorised access, alteration, disclosure, or destruction of your data. These measures include encrypted data transmission, access controls, and regular security reviews.

No method of transmission over the internet is completely secure, and we cannot guarantee absolute security. If you have concerns about the security of your information, please contact us at engage@aldersearch.com.`,
  },
  {
    title: "Changes to This Policy",
    content: `We may update this privacy policy from time to time to reflect changes in our practices or applicable law. The most current version will always be available on our website. We will notify you of material changes where we have your contact details and are required to do so by law.

This policy was last updated in October 2026.`,
  },
  {
    title: "Contact Us",
    content: `If you have any questions about this privacy policy or how we handle your personal information, please contact us:

Ryan Ollerenshaw, Founding Partner
Alder Search Ltd
71-75 Shelton Street, Covent Garden
London, WC2H 9JQ
engage@aldersearch.com`,
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    // Add noindex to prevent search engine indexing of privacy policy
    const noindex = document.createElement("meta");
    noindex.name = "robots";
    noindex.content = "noindex, nofollow";
    noindex.id = "privacy-noindex";
    document.head.appendChild(noindex);
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") ?? "";

    const privacyTitle = "Privacy Policy | Alder Search";
    const privacyDesc = "How Alder Search Ltd collects, uses, and protects your personal data in accordance with UK GDPR.";

    document.title = privacyTitle;
    if (metaDesc) metaDesc.setAttribute("content", privacyDesc);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOgTitle = ogTitle?.getAttribute("content") ?? "";
    const prevOgDesc = ogDesc?.getAttribute("content") ?? "";
    const prevOgUrl = ogUrl?.getAttribute("content") ?? "";

    ogTitle?.setAttribute("content", privacyTitle);
    ogDesc?.setAttribute("content", privacyDesc);
    ogUrl?.setAttribute("content", "https://aldersearch.com/privacy");

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
      ogTitle?.setAttribute("content", prevOgTitle);
      ogDesc?.setAttribute("content", prevOgDesc);
      ogUrl?.setAttribute("content", prevOgUrl);
      document.getElementById("privacy-noindex")?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.008 80)" }}>
      <Navigation />

      {/* Header */}
      <div
        className="pt-36 pb-16 px-8"
        style={{ background: "oklch(0.22 0.065 155)" }}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
            <span
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontSize: "0.7rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Legal
            </span>
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2rem, 4vw, 3rem)",
              color: "oklch(0.952 0.012 80)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              marginBottom: "1rem",
            }}
          >
            Privacy Policy
          </h1>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.62rem",
              color: "oklch(0.55 0.02 155)",
              letterSpacing: "0.06em",
            }}
          >
            Last updated: October 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-16">
        <div className="max-w-3xl mx-auto">
          {/* Intro */}
          <p
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "1.05rem",
              lineHeight: 1.85,
              color: "oklch(0.28 0.04 80)",
              marginBottom: "3rem",
              paddingBottom: "3rem",
              borderBottom: "1px solid oklch(0.88 0.01 80)",
            }}
          >
            Alder Search Ltd is committed to protecting your personal information and
            being transparent about how we use it. This privacy policy explains what
            data we collect, why we collect it, and how we handle it in accordance
            with the UK General Data Protection Regulation (UK GDPR) and the Data
            Protection Act 2018.
          </p>

          {/* Sections */}
          {sections.map((section, i) => (
            <div
              key={i}
              className="mb-10 pb-10"
              style={{
                borderBottom:
                  i < sections.length - 1
                    ? "1px solid oklch(0.91 0.008 80)"
                    : "none",
              }}
            >
              <h2
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  color: "oklch(0.22 0.06 155)",
                  letterSpacing: "-0.01em",
                  marginBottom: "1rem",
                }}
              >
                {section.title}
              </h2>
              {section.content.split("\n\n").map((para, j) => (
                <p
                  key={j}
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "0.95rem",
                    lineHeight: 1.85,
                    color: "oklch(0.32 0.03 80)",
                    marginBottom: "0.9rem",
                    whiteSpace: "pre-line",
                  }}
                >
                  {para}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
